import { useState } from 'react';

export default function Home() {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    phone: '',
    email: '',
    inquiry: '',
    message: '',
    privacyAccepted: false
  });

  const [step, setStep] = useState<'input' | 'confirm' | 'done'>('input');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validate = () => {
    if (!formData.privacyAccepted) {
      setSubmitStatus('error');
      return false;
    }

    // HTML required でも止まるけど、確認画面へ行く前に一応チェック
    if (
      !formData.companyName.trim() ||
      !formData.name.trim() ||
      !formData.phone.trim() ||
      !formData.email.trim() ||
      !formData.inquiry.trim()
    ) {
      setSubmitStatus('error');
      return false;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return false;
    }

    return true;
  };

  // 入力 → 確認
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitStatus('idle');
    if (!validate()) return;

    setStep('confirm');
  };

  // 確認 → 送信（send.php）
  const handleFinalSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('inquiry', formData.inquiry);
      formBody.append('message', formData.message);
      formBody.append('privacyAccepted', formData.privacyAccepted ? 'はい' : 'いいえ');

      // ★ 送信先を /send.php に変更
      const response = await fetch('/send.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setStep('done');
        setFormData({
          companyName: '',
          name: '',
          phone: '',
          email: '',
          inquiry: '',
          message: '',
          privacyAccepted: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToEdit = () => {
    setSubmitStatus('idle');
    setStep('input');
  };

  const handleRestart = () => {
    setSubmitStatus('idle');
    setStep('input');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent" style={{ fontFamily: '"Montserrat", sans-serif' }}>TCY</h1>
            <div className="flex gap-8 items-center">
              <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors font-medium cursor-pointer">サービス</a>
              <a href="#strengths" className="text-gray-700 hover:text-blue-600 transition-colors font-medium cursor-pointer">強み</a>
              <a href="#flow" className="text-gray-700 hover:text-blue-600 transition-colors font-medium cursor-pointer">導入の流れ</a>
              <a href="#about" className="text-gray-700 hover:text-blue-600 transition-colors font-medium cursor-pointer">会社情報</a>
              <a href="#contact" className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-full hover:shadow-xl transition-all whitespace-nowrap cursor-pointer">
                お問い合わせ
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Modern%20professional%20business%20office%20environment%20with%20diverse%20team%20collaborating%20on%20sales%20strategy%2C%20bright%20natural%20lighting%20streaming%20through%20large%20windows%2C%20contemporary%20workspace%20with%20laptops%20and%20documents%2C%20professional%20business%20people%20in%20suits%20discussing%20sales%20plans%2C%20clean%20minimalist%20design%2C%20corporate%20atmosphere%2C%20blue%20and%20white%20color%20scheme%2C%20high-end%20business%20setting%2C%20successful%20sales%20team%20meeting%2C%20professional%20photography%20style%2C%20realistic%20and%20sharp%20details&width=1920&height=1080&seq=hero-bg-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-cyan-500/20"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-20 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Content */}
            <div>
              <div className="inline-block px-5 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-bold mb-6">
                営業代行サービス
              </div>
              
              <h2 className="text-6xl font-black text-gray-900 mb-6 leading-tight">
                売上を加速する<br />
                <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">営業のプロ</span>
              </h2>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                株式会社TCYは、企業の営業活動を全面的にサポート。<br />
                新規開拓から既存顧客のフォローまで、<br />
                経験豊富な営業のプロフェッショナルが、<br />
                貴社の売上拡大を実現します。
              </p>

              <div className="flex gap-4 mb-12">
                <a href="#contact" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg font-bold rounded-full hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer">
                  無料相談を申し込む
                </a>
                <a href="#services" className="px-8 py-4 bg-white text-blue-600 text-lg font-bold rounded-full border-2 border-blue-600 hover:bg-blue-50 transition-all whitespace-nowrap cursor-pointer">
                  サービスを見る
                </a>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20business%20person%20in%20elegant%20suit%20presenting%20sales%20strategy%20with%20confidence%2C%20modern%20corporate%20office%20background%2C%20successful%20sales%20representative%20holding%20tablet%20or%20documents%2C%20bright%20professional%20lighting%2C%20business%20excellence%20and%20achievement%2C%20corporate%20professionalism%2C%20blue%20and%20white%20business%20attire%2C%20confident%20posture%2C%20high-quality%20business%20photography%2C%20realistic%20and%20professional%20style&width=700&height=800&seq=hero-person-001&orientation=portrait"
                  alt="営業代行サービス"
                  className="w-full h-[600px] object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full opacity-20 blur-3xl"></div>
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-gradient-to-br from-cyan-400 to-blue-400 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-bold text-sm tracking-wider uppercase mb-4 block">Services</span>
            <h3 className="text-5xl font-black text-gray-900 mb-6">提供サービス</h3>
            <p className="text-xl text-gray-600">
              企業の成長ステージに合わせた、最適な営業支援を提供します
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="group">
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 h-full">
                <div className="mb-6 rounded-2xl overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Professional%20business%20person%20making%20cold%20calls%20and%20prospecting%20new%20clients%2C%20modern%20office%20desk%20with%20laptop%20and%20phone%2C%20focused%20sales%20representative%20working%20on%20lead%20generation%2C%20professional%20business%20environment%2C%20bright%20office%20lighting%2C%20business%20development%20activities%2C%20corporate%20sales%20process%2C%20realistic%20business%20photography%2C%20professional%20and%20determined%20atmosphere&width=400&height=250&seq=service-newbiz-001&orientation=landscape"
                    alt="新規開拓営業"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-rocket-line text-3xl text-white"></i>
                </div>
                
                <h4 className="text-2xl font-black text-gray-900 mb-4">新規開拓営業</h4>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  ターゲット企業のリストアップから、アポイント獲得、商談、クロージングまで一貫してサポート。新規顧客の獲得を加速します。
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">テレアポ・メールアプローチ</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">商談・プレゼンテーション</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">クロージング支援</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 2 */}
            <div className="group">
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 h-full">
                <div className="mb-6 rounded-2xl overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Business%20professional%20nurturing%20client%20relationships%2C%20friendly%20meeting%20with%20existing%20customers%2C%20account%20management%20and%20customer%20success%20activities%2C%20professional%20handshake%20and%20warm%20business%20interaction%2C%20modern%20corporate%20meeting%20room%2C%20trust%20and%20partnership%20in%20business%2C%20customer%20retention%20and%20satisfaction%2C%20realistic%20business%20photography%2C%20professional%20and%20friendly%20atmosphere&width=400&height=250&seq=service-account-001&orientation=landscape"
                    alt="既存顧客フォロー"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-customer-service-2-line text-3xl text-white"></i>
                </div>
                
                <h4 className="text-2xl font-black text-gray-900 mb-4">既存顧客フォロー</h4>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  既存顧客との関係を深め、リピート受注やアップセルを実現。定期的なフォローで顧客満足度を向上させます。
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">定期訪問・ヒアリング</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">アップセル・クロスセル提案</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">顧客満足度向上施策</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 3 */}
            <div className="group">
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 h-full">
                <div className="mb-6 rounded-2xl overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Business%20team%20analyzing%20sales%20strategy%20and%20data%2C%20strategic%20planning%20meeting%20with%20charts%20and%20graphs%2C%20sales%20process%20optimization%20and%20consulting%2C%20professional%20business%20consultants%20reviewing%20performance%20metrics%2C%20modern%20conference%20room%20with%20presentation%20screens%2C%20data-driven%20decision%20making%2C%20corporate%20strategy%20session%2C%20realistic%20business%20photography%2C%20professional%20analytical%20atmosphere&width=400&height=250&seq=service-strategy-001&orientation=landscape"
                    alt="営業戦略立案"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-line-chart-line text-3xl text-white"></i>
                </div>
                
                <h4 className="text-2xl font-black text-gray-900 mb-4">営業戦略立案</h4>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  市場分析から営業プロセスの設計まで、データに基づいた戦略を立案。営業組織全体の生産性を向上させます。
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">市場・競合分析</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">営業プロセス設計</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">KPI設定・効果測定</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strengths Section */}
      <section id="strengths" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-bold text-sm tracking-wider uppercase mb-4 block">Strengths</span>
            <h3 className="text-5xl font-black text-gray-900 mb-6">TCYの強み</h3>
          </div>

          <div className="space-y-20">
            {/* Strength 1 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Experienced%20professional%20sales%20team%20in%20business%20suits%2C%20diverse%20group%20of%20expert%20sales%20representatives%2C%20confident%20and%20successful%20business%20professionals%2C%20corporate%20team%20portrait%20in%20modern%20office%2C%20professional%20business%20people%20with%20years%20of%20experience%2C%20high-achieving%20sales%20experts%2C%20realistic%20business%20photography%2C%20professional%20and%20trustworthy%20appearance%2C%20blue%20and%20gray%20business%20attire&width=600&height=400&seq=strength-expert-001&orientation=landscape"
                    alt="経験豊富な営業のプロ"
                    className="w-full h-96 object-cover"
                  />
                </div>
              </div>
              <div>
                <div className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-bold mb-4">
                  Strength 01
                </div>
                <h4 className="text-4xl font-black text-gray-900 mb-6">経験豊富な営業のプロ</h4>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  大手企業での営業経験を持つプロフェッショナルが多数在籍。BtoB、BtoC問わず、あらゆる業界・商材の営業に対応可能です。豊富な実績とノウハウで、確実に成果を出します。
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-blue-600"></i>
                    </div>
                    <span className="text-gray-700">大手企業出身の営業経験者</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-blue-600"></i>
                    </div>
                    <span className="text-gray-700">業界・商材を問わない対応力</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Strength 2 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="inline-block px-4 py-2 bg-cyan-100 text-cyan-600 rounded-full text-sm font-bold mb-4">
                  Strength 02
                </div>
                <h4 className="text-4xl font-black text-gray-900 mb-6">データドリブンな営業支援</h4>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  最新のCRMツールとデータ分析を活用し、営業活動を可視化。PDCAサイクルを高速で回し、継続的な改善を実現します。勘や経験だけに頼らない、科学的な営業手法を提供します。
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-cyan-600"></i>
                    </div>
                    <span className="text-gray-700">CRMツールによる活動管理</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-cyan-600"></i>
                    </div>
                    <span className="text-gray-700">データ分析に基づく改善提案</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-cyan-600"></i>
                    </div>
                    <span className="text-gray-700">定期的なレポーティング</span>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Business%20analytics%20dashboard%20on%20computer%20screens%2C%20sales%20data%20visualization%20with%20charts%20and%20graphs%2C%20CRM%20system%20interface%20showing%20sales%20metrics%20and%20KPIs%2C%20modern%20data-driven%20sales%20management%2C%20professional%20business%20person%20analyzing%20sales%20performance%20data%2C%20digital%20transformation%20in%20sales%2C%20realistic%20business%20technology%20photography%2C%20clean%20and%20professional%20workspace&width=600&height=400&seq=strength-data-001&orientation=landscape"
                    alt="データドリブンな営業"
                    className="w-full h-96 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Strength 3 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Flexible%20business%20solutions%20and%20customized%20services%2C%20business%20consultant%20presenting%20tailored%20strategy%20to%20client%2C%20personalized%20business%20approach%20and%20adaptation%2C%20professional%20meeting%20discussing%20custom%20solutions%2C%20modern%20corporate%20environment%2C%20collaborative%20business%20partnership%2C%20realistic%20business%20photography%2C%20professional%20and%20accommodating%20atmosphere%2C%20client-focused%20service&width=600&height=400&seq=strength-flexible-001&orientation=landscape"
                    alt="柔軟な対応力"
                    className="w-full h-96 object-cover"
                  />
                </div>
              </div>
              <div>
                <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-600 rounded-full text-sm font-bold mb-4">
                  Strength 03
                </div>
                <h4 className="text-4xl font-black text-gray-900 mb-6">柔軟な対応力</h4>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  企業の規模や業種、予算に合わせて最適なプランをご提案。短期的なプロジェクトから長期的なパートナーシップまで、柔軟に対応します。貴社の営業チームの一員として、伴走します。
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-indigo-600"></i>
                    </div>
                    <span className="text-gray-700">企業規模・業種を問わない対応</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-indigo-600"></i>
                    </div>
                    <span className="text-gray-700">予算に応じた最適プラン</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-indigo-600"></i>
                    </div>
                    <span className="text-gray-700">短期・長期どちらも対応可能</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flow Section */}
      <section id="flow" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-bold text-sm tracking-wider uppercase mb-4 block">Flow</span>
            <h3 className="text-5xl font-black text-gray-900 mb-6">導入の流れ</h3>
            <p className="text-xl text-gray-600">
              スムーズな導入で、すぐに営業活動をスタートできます
            </p>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-cyan-200 to-blue-200"></div>

            <div className="grid md:grid-cols-4 gap-8 relative">
              {/* Step 1 */}
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto shadow-xl relative z-10">
                    <i className="ri-chat-3-line text-5xl text-white"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-black text-lg shadow-lg">
                    1
                  </div>
                </div>
                <h4 className="text-2xl font-black text-gray-900 mb-3">無料相談</h4>
                <p className="text-gray-600 leading-relaxed">
                  現状の課題や目標をヒアリング
                </p>
              </div>

              {/* Step 2 */}
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-32 h-32 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto shadow-xl relative z-10">
                    <i className="ri-file-list-3-line text-5xl text-white"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-black text-lg shadow-lg">
                    2
                  </div>
                </div>
                <h4 className="text-2xl font-black text-gray-900 mb-3">提案</h4>
                <p className="text-gray-600 leading-relaxed">
                  最適なプランと見積もりをご提示
                </p>
              </div>

              {/* Step 3 */}
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-32 h-32 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto shadow-xl relative z-10">
                    <i className="ri-settings-3-line text-5xl text-white"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-black text-lg shadow-lg">
                    3
                  </div>
                </div>
                <h4 className="text-2xl font-black text-gray-900 mb-3">準備</h4>
                <p className="text-gray-600 leading-relaxed">
                  営業資料の準備と戦略の策定
                </p>
              </div>

              {/* Step 4 */}
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto shadow-xl relative z-10">
                    <i className="ri-rocket-2-line text-5xl text-white"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-black text-lg shadow-lg">
                    4
                  </div>
                </div>
                <h4 className="text-2xl font-black text-gray-900 mb-3">開始</h4>
                <p className="text-gray-600 leading-relaxed">
                  営業活動スタート・定期報告
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-bold text-sm tracking-wider uppercase mb-4 block">Contact</span>
            <h3 className="text-5xl font-black text-gray-900 mb-6">お問い合わせ</h3>
            <p className="text-xl text-gray-600">
              営業代行に関するご相談は、お気軽にお問い合わせください
            </p>
          </div>

          <div className="bg-white rounded-3xl p-10 shadow-2xl border border-gray-100">
            {/* ===== 入力 ===== */}
            {step === 'input' && (
              <form onSubmit={handleSubmit} data-readdy-form id="tcy-contact-form">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">会社名 <span className="text-blue-600">*</span></label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-sm"
                      placeholder="株式会社〇〇"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">お名前 <span className="text-blue-600">*</span></label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-sm"
                      placeholder="山田 太郎"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">電話番号 <span className="text-blue-600">*</span></label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-sm"
                      placeholder="03-1234-5678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">メールアドレス <span className="text-blue-600">*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-sm"
                      placeholder="example@company.com"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 mb-2">お問い合わせ内容 <span className="text-blue-600">*</span></label>
                  <select
                    name="inquiry"
                    value={formData.inquiry}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 pr-8 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-sm cursor-pointer appearance-none"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%232563eb'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                  >
                    <option value="" className="bg-white">選択してください</option>
                    <option value="新規開拓営業について" className="bg-white">新規開拓営業について</option>
                    <option value="既存顧客フォローについて" className="bg-white">既存顧客フォローについて</option>
                    <option value="営業戦略立案について" className="bg-white">営業戦略立案について</option>
                    <option value="料金について" className="bg-white">料金について</option>
                    <option value="その他" className="bg-white">その他</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 mb-2">詳細・ご質問</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all resize-none text-sm"
                    placeholder="ご質問やご要望をご記入ください（500文字以内）"
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">{formData.message.length}/500</div>
                </div>

                <div className="mb-8">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="privacyAccepted"
                      checked={formData.privacyAccepted}
                      onChange={handleChange}
                      required
                      className="mt-1 w-5 h-5 rounded border-gray-300 bg-gray-50 text-blue-600 focus:ring-2 focus:ring-blue-600 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 leading-relaxed group-hover:text-gray-800 transition-colors">
                      <a href="/privacy" className="text-blue-600 hover:text-blue-700 underline">プライバシーポリシー</a>に同意します <span className="text-blue-600">*</span>
                    </span>
                  </label>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-2xl text-green-700 text-sm">
                    <i className="ri-checkbox-circle-line mr-2"></i>
                    お問い合わせを受け付けました。担当者より折り返しご連絡いたします。
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl text-red-700 text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    送信に失敗しました。必須項目とプライバシーポリシーへの同意をご確認の上、もう一度お試しください。
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg font-bold rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  確認画面へ
                </button>
              </form>
            )}

            {/* ===== 確認 ===== */}
            {step === 'confirm' && (
              <div>
                <div className="mb-6">
                  <div className="text-sm font-bold text-gray-700 mb-2">入力内容の確認</div>
                  <div className="text-sm text-gray-600">以下の内容で送信します。問題なければ「送信する」を押してください。</div>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-4 text-sm">
                    <div className="text-gray-500 font-bold mb-1">会社名</div>
                    <div className="text-gray-800 font-medium">{formData.companyName}</div>
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-4 text-sm">
                    <div className="text-gray-500 font-bold mb-1">お名前</div>
                    <div className="text-gray-800 font-medium">{formData.name}</div>
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-4 text-sm">
                    <div className="text-gray-500 font-bold mb-1">電話番号</div>
                    <div className="text-gray-800 font-medium">{formData.phone}</div>
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-4 text-sm">
                    <div className="text-gray-500 font-bold mb-1">メールアドレス</div>
                    <div className="text-gray-800 font-medium">{formData.email}</div>
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-4 text-sm">
                    <div className="text-gray-500 font-bold mb-1">お問い合わせ内容</div>
                    <div className="text-gray-800 font-medium">{formData.inquiry}</div>
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-4 text-sm">
                    <div className="text-gray-500 font-bold mb-1">詳細・ご質問</div>
                    <div className="text-gray-800 font-medium whitespace-pre-wrap">{formData.message || '（未入力）'}</div>
                    <div className="text-right text-sm text-gray-500 mt-2">{formData.message.length}/500</div>
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-4 text-sm">
                    <div className="text-gray-500 font-bold mb-1">プライバシーポリシー</div>
                    <div className="text-gray-800 font-medium">{formData.privacyAccepted ? '同意する' : '同意しない'}</div>
                  </div>
                </div>

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl text-red-700 text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    送信に失敗しました。時間をおいて再度お試しください。
                  </div>
                )}

                <div className="flex flex-col md:flex-row gap-4">
                  <button
                    type="button"
                    onClick={handleBackToEdit}
                    disabled={isSubmitting}
                    className="w-full md:w-1/2 py-4 bg-white text-blue-600 text-lg font-bold rounded-full border-2 border-blue-600 hover:bg-blue-50 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    戻って修正する
                  </button>

                  <button
                    type="button"
                    onClick={handleFinalSubmit}
                    disabled={isSubmitting}
                    className="w-full md:w-1/2 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg font-bold rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                </div>
              </div>
            )}

            {/* ===== 完了 ===== */}
            {step === 'done' && (
              <div>
                <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-2xl text-green-700 text-sm">
                  <i className="ri-checkbox-circle-line mr-2"></i>
                  お問い合わせを受け付けました。担当者より折り返しご連絡いたします。
                </div>

                <button
                  type="button"
                  onClick={handleRestart}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg font-bold rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  もう一度送信する
                </button>
              </div>
            )}
          </div>

          {/* Contact Info Cards */}
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <i className="ri-phone-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-gray-900 mb-2 text-center">お電話でのお問い合わせ</h4>
              <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent text-center mb-2">090-2162-7360</p>
              <p className="text-sm text-gray-600 text-center">受付時間：平日 9:00〜18:00</p>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <i className="ri-mail-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-gray-900 mb-2 text-center">メールでのお問い合わせ</h4>
              <p className="text-lg font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent text-center mb-2">tcy.sales@tcy-sales.com</p>
              <p className="text-sm text-gray-600 text-center">24時間受付</p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 shadow-xl border border-gray-100">
            <h3 className="text-4xl font-black text-gray-900 mb-12 text-center">会社情報</h3>
            <div className="space-y-6">
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <i className="ri-building-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">会社名</div>
                  <div className="text-xl font-bold text-gray-900">株式会社TCY</div>
                </div>
              </div>
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <i className="ri-calendar-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">設立</div>
                  <div className="text-xl font-bold text-gray-900">令和6年11月18日</div>
                </div>
              </div>
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">所在地</div>
                  <div className="text-xl font-bold text-gray-900">静岡県伊東市富戸８７７番地の４</div>
                </div>
              </div>
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <i className="ri-briefcase-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">事業内容</div>
                  <div className="text-lg text-gray-700">
                    <div className="font-bold text-gray-900 mb-2">営業代行業務</div>
                    <div className="space-y-2">
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span>新規開拓営業の代行</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span>既存顧客フォロー業務</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span>営業戦略の立案・実行支援</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4" style={{ fontFamily: '"Montserrat", sans-serif' }}>TCY</div>
            <p className="text-gray-400 mb-8">売上を加速する営業のプロ</p>
            <div className="flex justify-center gap-8 mb-8 flex-wrap">
              <a href="#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">サービス</a>
              <a href="#strengths" className="text-gray-400 hover:text-white transition-colors cursor-pointer">強み</a>
              <a href="#flow" className="text-gray-400 hover:text-white transition-colors cursor-pointer">導入の流れ</a>
              <a href="#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">会社情報</a>
              <a href="#contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer">お問い合わせ</a>
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a>
            </div>
            <div className="border-t border-gray-700 pt-8">
              <p className="text-gray-400 text-sm">
                &copy; 2024 株式会社TCY. All rights reserved. 
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
